/* eslint-disable consistent-return */
import store from '@/store'
import { EndpointDecoder } from '@sword-health/ui-http-mapper'
import axios from 'axios'
import { endpointsSpec, generateAuthTokens } from '@/http/endpoints'
import { CONFIG } from '@/scripts/app-configs/constants'
import '@/http/interceptors/request-interceptors'
import '@/http/interceptors/response-interceptors'
import getLocalFiles from '@/scripts/tools/get-local-files'
import { $date } from '@/plugins'

const swordClinicalPortalEndpointInterface = new EndpointDecoder(endpointsSpec)

let storeSubscribed = false

function setupStoreSubscription() {
  if (storeSubscribed || !store?.subscribe) return

  store.subscribe((mutation) => {
    const userTokensChanges = ['user/authentication/setAuthTokens', 'user/authentication/loadStoredAuthTokens', 'user/resetProfile']

    if (userTokensChanges.includes(mutation.type)) {
      swordClinicalPortalEndpointInterface.dataUpdate = { headers: generateAuthTokens() }
    }
  })

  // Set initial auth headers from current store state
  swordClinicalPortalEndpointInterface.dataUpdate = { headers: generateAuthTokens() }
  storeSubscribed = true
}

axios.defaults.baseURL = CONFIG.baseUrl

function axiosWrapper(method, url, ...args) {
  const configIndex = args.length - 1
  const source = axios.CancelToken.source()

  args[configIndex] = Object.assign(args[configIndex], {
    cancelToken: source.token,
  })

  const request = axios[method](url, ...args)

  request.cancel = source.cancel

  return request
}

function swordHttpClient(...args) {
  setupStoreSubscription()

  const endpointConfig = swordClinicalPortalEndpointInterface.$http(...args)

  if (!endpointConfig) {
    return
  }

  const { method, url, requestArgs, meta } = endpointConfig

  // necessary for HG to work with the current demo env, to be removed when new demo is ready
  if ((CONFIG.useHttpMocks || meta?.forceMock) && process.env.NODE_ENV !== 'test') {
    if (!meta?.mock) {
      console.error(`No mockData found for endpoint ${url} - ${args[0]}`)

      return Promise.reject(new Error(`No mockData found for endpoint ${url}`))
    }

    if (typeof meta.mock !== 'function') {
      console.error(`Mock needs to be a function for endpoint ${url} - ${args[0]}`)

      return Promise.reject(new Error(`Invalid mockData function for endpoint ${url}`))
    }

    const [defPath, requestData, requestConfigs] = args
    const requestBody = requestConfigs?.body
    const mockData = meta.mock(defPath, requestData, requestBody)

    if (!mockData) {
      console.error(`Empty mockData found for endpoint ${url} - ${args[0]}`)

      return Promise.reject(new Error(`Empty mockData found for endpoint ${url} - ${args[0]}`))
    }

    if (typeof mockData === 'object') {
      return Promise.resolve({ data: mockData })
    }

    const localStaticURL = '/static/mocks'

    return getLocalFiles(mockData).then((mockObj) => {
      let mockDataString = JSON.stringify(mockObj.data)

      mockDataString = mockDataString.replaceAll('[LOCAL_STATIC_URL]', localStaticURL)
        .replaceAll(
          /\[DATE_([0-9]+)_(DAY|MONTH|YEAR)_(AGO|AHEAD)\](?:\[([^\]]+)\])?/g,
          (str, amount, frame, verb, format) => {
            /*
            * Example: [DATE_3_DAY_AGO][YYYY-MM-DD HH:mm:ss]
            */
            const now = new Date()
            const operator = verb === 'AGO' ? -1 : 1
            const directionalAmount = amount * operator

            if (frame === 'DAY') {
              now.setDate(now.getDate() + directionalAmount)
            } else if (frame === 'MONTH') {
              now.setMonth(now.getMonth() + directionalAmount)
            } else if (frame === 'YEAR') {
              now.setFullYear(now.getFullYear() + directionalAmount)
            }

            return format ? $date(now).format(format) : now.getTime()
          },
        )
      mockObj.data = JSON.parse(mockDataString)

      const errorProps = ['error_code', 'error_description']
      const errorMock = Object.keys(mockObj.data).some((mp) => {
        const errorPropUsed = errorProps.includes(mp)
        const accessDenied = mp === 'access' && mockObj.data.access === 'denied'

        return errorPropUsed || accessDenied
      })

      if (errorMock) {
        mockObj.response = { data: mockObj.data }
        delete mockObj.data

        return Promise.reject(mockObj)
      }

      return Promise.resolve(mockObj)
    })
  }

  const config = requestArgs[1]

  if (config?.headers && config['DPT-Activity-UUID']) {
    config.headers['DPT-Activity-UUID'] = config['DPT-Activity-UUID']
  }

  if (method === 'delete') {
    const [body] = requestArgs

    config.data = body

    return axiosWrapper(method, url, config)
  }

  return axiosWrapper(method, url, ...requestArgs)
}

export { swordHttpClient as $http }

export default {
  install(app) {
    app.config.globalProperties.$http = swordHttpClient
  },
}
